/**
 * ShipmentBatchPage 裝櫃批次 (S6.1) — Board D #6, 船務 Shipping nav.
 *
 * Split out of the codex `ShippingPages.tsx` onto the house list stack
 * (useListPage + GenericFilterPopover + TableContainer state triad).
 *
 * Salvaged gates (server-enforced in mocks/handlers/shipping.ts):
 * - 封櫃截單 cut-off requires 櫃號 + 封條 (BatchCutOffModal mirrors the 400)
 * - qtyLoaded ≤ qtyPlanned per line
 * - short-load at seal → owned 訂單異常 (owner 船務, due window)
 * - 離港 depart requires cut_off — the action only renders on sealed batches
 *   (hide-don't-disable), so the server 409 never surfaces in normal flow
 * - loaded quantities are FIXED at seal — the 進貨 path never rewrites them
 *
 * Short-loaded sealed batches render the numeric gap + a warning glyph
 * (never colour alone). The OCC 裝櫃批次 lens reads the same batch facts.
 */

import { useMemo, useState } from 'react';
import { App, Badge, Button, Dropdown, Input, Progress, Table, Tag, Typography, theme } from 'antd';
import type { TableProps } from 'antd';
import {
  ContainerOutlined,
  EditOutlined,
  EyeOutlined,
  FilterOutlined,
  MoreOutlined,
  PlusOutlined,
  ProfileOutlined,
  SearchOutlined,
  SendOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import TableContainer, { ListEmptyState, renderDash } from '@/components/common/TableContainer';
import GenericFilterPopover from '@/components/common/GenericFilterPopover';
import type { FieldConfig, FilterValues } from '@/components/common/GenericFilterPopover';
import { HighlightText } from '@/components/common/HighlightText';
import { ConfirmationDialog } from '@/components/common/ConfirmationDialog';
import { useListPage } from '@/hooks/useListPage';
import { extractApiError } from '@/hooks/useSalesOrders';
import { useShipmentBatchesTable, useShipmentBatchMutations } from '@/hooks/useShipping';
import { useShippingStore } from '@/stores/shippingStore';
import type { BatchFilters, BatchStatusFilter } from '@/stores/shippingStore';
import { controlHeightTokens, radiusTokens } from '@/styles/tokens';
import type { ShipmentBatch, ShipmentBatchStatus } from '@/types/api/shipping';
import { toCaptured, totalCbm, utilizationPct } from './batchCapture';
import BatchCreateModal from './components/BatchCreateModal';
import BatchCutOffModal from './components/BatchCutOffModal';
import BatchManifestDrawer from './components/BatchManifestDrawer';
import BatchQuantityCorrectionModal from './components/BatchQuantityCorrectionModal';

const { Text } = Typography;

/** antd Tag presets per status — matches the OCC BatchLens mapping. */
const STATUS_TAG_COLOR: Record<ShipmentBatchStatus, string> = {
  open: 'default',
  cut_off: 'orange',
  departed: 'cyan',
  closed: 'green',
};

const BATCH_STATUS_ORDER: ShipmentBatchStatus[] = ['open', 'cut_off', 'departed', 'closed'];

interface BatchFilterPopoverValues extends FilterValues {
  status?: string;
}

const ShipmentBatchPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation(['shipping', 'common']);
  const { token } = theme.useToken();
  const { message } = App.useApp();

  const { batchFilters, setBatchSearch, setBatchStatus, resetBatchFilters } = useShippingStore();
  const [filterPopoverOpen, setFilterPopoverOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  // Record + open are decoupled so the closing animation keeps its content.
  const [cutOffBatchRow, setCutOffBatchRow] = useState<ShipmentBatch | null>(null);
  const [cutOffOpen, setCutOffOpen] = useState(false);
  const [departBatchRow, setDepartBatchRow] = useState<ShipmentBatch | null>(null);
  const [departOpen, setDepartOpen] = useState(false);
  const [departing, setDeparting] = useState(false);
  // S6.2 — 裝櫃明細 manifest drawer + 改數量 correction loop
  const [manifestBatchRow, setManifestBatchRow] = useState<ShipmentBatch | null>(null);
  const [manifestOpen, setManifestOpen] = useState(false);
  const [correctionBatchRow, setCorrectionBatchRow] = useState<ShipmentBatch | null>(null);
  const [correctionOpen, setCorrectionOpen] = useState(false);

  const customFilterCounters = useMemo(
    () => ({ status: (value: unknown) => (value === 'all' ? 0 : 1) }),
    []
  );

  const { tableProps, isLoading, refresh, activeFilterCount, isFiltered, listState } = useListPage<
    BatchFilters,
    ShipmentBatch
  >({
    useTableHook: useShipmentBatchesTable,
    filters: batchFilters,
    filterCountExclusions: ['search'],
    customFilterCounters,
  });

  const { departBatch } = useShipmentBatchMutations();

  const handleDepart = async () => {
    if (!departBatchRow) return;
    setDeparting(true);
    try {
      await departBatch(departBatchRow.id);
      message.success(t('shipping:batch.messages.departed'));
      setDepartOpen(false);
      refresh();
    } catch (error) {
      const body = extractApiError(error);
      message.error(body.message || t('common:messages.updateFailed'));
    } finally {
      setDeparting(false);
    }
  };

  const filterFields: FieldConfig[] = useMemo(
    () => [
      {
        key: 'status',
        label: t('shipping:batch.fields.status'),
        type: 'radio-cards',
        defaultValue: 'all',
        options: [
          { value: 'all', label: t('shipping:batch.filters.allStatuses') },
          ...BATCH_STATUS_ORDER.map((status) => ({
            value: status,
            label: t(`shipping:batch.status.${status}`),
          })),
        ],
        resetText: t('common:actions.reset'),
      },
    ],
    [t]
  );

  const columns: TableProps<ShipmentBatch>['columns'] = [
    {
      title: t('shipping:batch.fields.batchNumber'),
      dataIndex: 'batchNumber',
      key: 'batchNumber',
      width: 210,
      render: (_: string, record) => (
        <div className="tw-flex tw-flex-col tw-gap-0.5">
          <span style={{ fontWeight: token.fontWeightStrong }}>
            <HighlightText
              text={record.batchNumber}
              searchTerm={batchFilters.search}
              fallback="-"
            />
          </span>
          <span style={{ fontSize: token.fontSizeSM, color: token.colorTextSecondary }}>
            <HighlightText
              text={record.vesselOrCarrier}
              searchTerm={batchFilters.search}
              fallback=""
            />
          </span>
        </div>
      ),
    },
    {
      title: t('shipping:batch.fields.containerSeal'),
      key: 'containerSeal',
      width: 170,
      render: (_: unknown, record) =>
        record.containerNumber ? (
          <div className="tw-flex tw-flex-col tw-gap-0.5">
            <Tag style={{ margin: 0, width: 'fit-content' }}>
              <HighlightText
                text={record.containerNumber}
                searchTerm={batchFilters.search}
                fallback=""
              />
            </Tag>
            <span style={{ fontSize: token.fontSizeSM, color: token.colorTextSecondary }}>
              {record.sealNumber ?? '-'}
            </span>
          </div>
        ) : (
          renderDash()
        ),
    },
    {
      title: t('shipping:batch.fields.mode'),
      key: 'mode',
      width: 120,
      render: (_: unknown, record) => {
        const captured = toCaptured(record);
        return (
          <div className="tw-flex tw-flex-col tw-gap-0.5">
            <Tag style={{ margin: 0, width: 'fit-content' }}>
              {t(`shipping:batch.mode.${captured.mode}`)}
            </Tag>
            {captured.containerType && (
              <span style={{ fontSize: token.fontSizeSM, color: token.colorTextSecondary }}>
                {t(`shipping:batch.containerType.${captured.containerType}`)}
              </span>
            )}
          </div>
        );
      },
    },
    {
      title: t('shipping:batch.fields.etd'),
      dataIndex: 'etd',
      key: 'etd',
      width: 120,
      render: (value: string) => renderDash(value),
    },
    {
      title: t('shipping:batch.fields.eta'),
      dataIndex: 'eta',
      key: 'eta',
      width: 120,
      render: (value: string) => renderDash(value),
    },
    {
      title: t('shipping:batch.fields.loaded'),
      key: 'loaded',
      width: 150,
      align: 'right',
      render: (_: unknown, record) => {
        if (record.lines.length === 0) return renderDash();
        const planned = record.lines.reduce((sum, line) => sum + line.qtyPlanned, 0);
        const loaded = record.lines.reduce((sum, line) => sum + line.qtyLoaded, 0);
        // Loaded is only meaningful once fixed at seal (open batches plan only).
        const sealed = record.status !== 'open';
        const short = sealed && loaded < planned;
        return (
          <span
            style={{
              fontVariantNumeric: 'tabular-nums',
              color: short ? token.colorWarning : undefined,
            }}
          >
            {short && (
              <WarningOutlined
                aria-label={t('shipping:batch.shortLoadTag')}
                style={{ marginRight: token.marginXXS }}
              />
            )}
            {sealed ? loaded : '-'} / {planned}
          </span>
        );
      },
    },
    {
      // S6.2 — CBM roll-up + utilization vs 櫃型 capacity (numbers, not colour)
      title: t('shipping:batch.fields.cbm'),
      key: 'cbm',
      width: 150,
      align: 'right',
      render: (_: unknown, record) => {
        const captured = toCaptured(record);
        const cbm = totalCbm(captured.lines);
        if (cbm === 0) return renderDash();
        const pct = utilizationPct(cbm, captured.containerType);
        return (
          <div className="tw-flex tw-flex-col tw-gap-0.5" style={{ alignItems: 'flex-end' }}>
            <span style={{ fontVariantNumeric: 'tabular-nums' }}>{cbm.toFixed(2)}</span>
            {pct != null && (
              <div className="tw-flex tw-items-center tw-gap-1" style={{ width: '100%' }}>
                <Progress
                  percent={Math.min(100, pct)}
                  showInfo={false}
                  size="small"
                  status={pct > 100 ? 'exception' : 'normal'}
                  style={{ flex: 1, margin: 0 }}
                />
                <span
                  style={{
                    fontSize: token.fontSizeSM,
                    color: token.colorTextSecondary,
                    fontVariantNumeric: 'tabular-nums',
                  }}
                >
                  {pct}%
                </span>
              </div>
            )}
          </div>
        );
      },
    },
    {
      title: t('shipping:batch.fields.pos'),
      dataIndex: 'poIds',
      key: 'poIds',
      width: 110,
      align: 'right',
      render: (poIds: string[]) => (
        <span style={{ fontVariantNumeric: 'tabular-nums' }}>{poIds.length}</span>
      ),
    },
    {
      title: t('shipping:batch.fields.status'),
      dataIndex: 'status',
      key: 'status',
      width: 110,
      render: (status: ShipmentBatchStatus) => (
        <Tag color={STATUS_TAG_COLOR[status]} style={{ margin: 0 }}>
          {t(`shipping:batch.status.${status}`)}
        </Tag>
      ),
    },
    {
      title: t('shipping:batch.fields.actions'),
      key: 'actions',
      width: 80,
      fixed: 'right',
      render: (_: unknown, record) => {
        // hide-don't-disable: each status affords exactly its legal next step
        const items = [
          ...(record.status === 'open'
            ? [
                {
                  key: 'cutOff',
                  label: t('shipping:batch.actions.cutOff'),
                  icon: <ContainerOutlined />,
                  onClick: (e: { domEvent: { stopPropagation: () => void } }) => {
                    e.domEvent.stopPropagation();
                    setCutOffBatchRow(record);
                    setCutOffOpen(true);
                  },
                },
              ]
            : []),
          ...(record.status === 'cut_off'
            ? [
                {
                  key: 'depart',
                  label: t('shipping:batch.actions.depart'),
                  icon: <SendOutlined />,
                  onClick: (e: { domEvent: { stopPropagation: () => void } }) => {
                    e.domEvent.stopPropagation();
                    setDepartBatchRow(record);
                    setDepartOpen(true);
                  },
                },
              ]
            : []),
          {
            key: 'viewManifest',
            label: t('shipping:batch.actions.viewManifest'),
            icon: <ProfileOutlined />,
            onClick: (e: { domEvent: { stopPropagation: () => void } }) => {
              e.domEvent.stopPropagation();
              setManifestBatchRow(record);
              setManifestOpen(true);
            },
          },
          // 改數量 correction loop — sealed batches only (hide-don't-disable)
          ...(record.status === 'cut_off' || record.status === 'departed'
            ? [
                {
                  key: 'correctQty',
                  label: t('shipping:batch.actions.correctQty'),
                  icon: <EditOutlined />,
                  onClick: (e: { domEvent: { stopPropagation: () => void } }) => {
                    e.domEvent.stopPropagation();
                    setCorrectionBatchRow(record);
                    setCorrectionOpen(true);
                  },
                },
              ]
            : []),
          {
            key: 'viewInOcc',
            label: t('shipping:batch.actions.viewInOcc'),
            icon: <EyeOutlined />,
            onClick: (e: { domEvent: { stopPropagation: () => void } }) => {
              e.domEvent.stopPropagation();
              navigate('/auth/occ/batches');
            },
          },
        ];
        return (
          <Dropdown menu={{ items }} trigger={['click']}>
            <Button
              type="text"
              aria-label={t('shipping:batch.actions.actionsFor', {
                batchNumber: record.batchNumber,
              })}
              icon={<MoreOutlined />}
              onClick={(e) => e.stopPropagation()}
            />
          </Dropdown>
        );
      },
    },
  ];

  return (
    <div className="tw-px-6 tw-pb-8">
      <div className="tw-mb-6">
        <h1 style={{ fontSize: token.fontSizeHeading2, fontWeight: token.fontWeightStrong }}>
          {t('shipping:batch.list.title')}
        </h1>
      </div>

      {/* Search + status filter + create toolbar */}
      <div className="tw-mb-6 tw-flex tw-gap-3 tw-items-center">
        <Input
          placeholder={t('shipping:batch.searchPlaceholder')}
          value={batchFilters.search}
          onChange={(e) => setBatchSearch(e.target.value)}
          variant="outlined"
          size="large"
          suffix={<SearchOutlined style={{ fontSize: token.sizeMD, color: token.colorPrimary }} />}
          style={{
            width: '450px',
            borderRadius: radiusTokens.card,
            height: `${controlHeightTokens.controlHeightXL}px`,
          }}
        />
        <GenericFilterPopover<BatchFilterPopoverValues>
          open={filterPopoverOpen}
          onOpenChange={setFilterPopoverOpen}
          onApply={(values) => setBatchStatus((values.status as BatchStatusFilter) || 'all')}
          initialFilters={{ status: batchFilters.status }}
          fields={filterFields}
          applyText={t('common:actions.apply')}
          cancelText={t('common:actions.cancel')}
        >
          <Badge count={activeFilterCount}>
            <Button
              aria-label={t('common:actions.filter')}
              icon={<FilterOutlined style={{ color: token.colorPrimary }} />}
              size="large"
              style={{
                borderRadius: radiusTokens.card,
                height: `${controlHeightTokens.controlHeightXL}px`,
                width: `${controlHeightTokens.controlHeightXL}px`,
              }}
            />
          </Badge>
        </GenericFilterPopover>
        <div className="tw-flex tw-gap-3" style={{ marginLeft: 'auto' }}>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            size="large"
            onClick={() => setCreateOpen(true)}
            style={{
              borderRadius: radiusTokens.card,
              height: `${controlHeightTokens.controlHeightXL}px`,
              paddingLeft: token.paddingLG,
              paddingRight: token.paddingLG,
            }}
          >
            {t('shipping:batch.addBatch')}
          </Button>
        </div>
      </div>

      {/* Table — S0.3 state triad via TableContainer + ListEmptyState */}
      <TableContainer
        errorState={{
          active: listState === 'error',
          title: t('shipping:batch.states.loadFailed'),
          retryLabel: t('common:errors.boundary.retry'),
          onRetry: refresh,
        }}
      >
        <Table<ShipmentBatch>
          {...tableProps}
          columns={columns}
          rowKey="id"
          loading={isLoading}
          locale={{
            emptyText: isFiltered ? (
              <ListEmptyState
                title={t('shipping:batch.states.noResults')}
                actionLabel={t('shipping:batch.states.clearFilters')}
                onAction={resetBatchFilters}
                actionType="default"
              />
            ) : (
              <ListEmptyState
                title={t('shipping:batch.states.noBatches')}
                actionLabel={t('shipping:batch.addBatch')}
                onAction={() => setCreateOpen(true)}
                actionIcon={<PlusOutlined />}
              />
            ),
          }}
          pagination={{ ...tableProps.pagination, showSizeChanger: false }}
          scroll={{ x: 1340 }}
        />
      </TableContainer>

      <BatchCreateModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={refresh}
      />
      <BatchCutOffModal
        open={cutOffOpen}
        batch={cutOffBatchRow}
        onClose={() => setCutOffOpen(false)}
        onDone={refresh}
      />
      <BatchManifestDrawer
        open={manifestOpen}
        batch={manifestBatchRow}
        onClose={() => setManifestOpen(false)}
      />
      <BatchQuantityCorrectionModal
        open={correctionOpen}
        batch={correctionBatchRow}
        onClose={() => setCorrectionOpen(false)}
        onDone={refresh}
      />
      <ConfirmationDialog
        open={departOpen}
        onClose={() => setDepartOpen(false)}
        onConfirm={() => void handleDepart()}
        title={t('shipping:batch.departConfirm.title')}
        message={
          <Text>
            {t('shipping:batch.departConfirm.message', {
              batchNumber: departBatchRow?.batchNumber ?? '',
              containerNumber: departBatchRow?.containerNumber ?? '-',
            })}
          </Text>
        }
        type="warning"
        confirmText={t('shipping:batch.departConfirm.confirm')}
        cancelText={t('common:actions.cancel')}
        loading={departing}
      />
    </div>
  );
};

export default ShipmentBatchPage;
